// FTEDN Team — Flag Row component
// Renders a single flag with type-appropriate editor (toggle, slider, dropdown).

import {
  type FlagDef,
  type FlagName,
  validateFlagValue,
} from '@ftedn/shared';

interface FlagRowProps {
  name: FlagName;
  def: FlagDef;
  currentValue: unknown;
  onUpdate: (name: FlagName, value: unknown) => void;
  onReset: (name: FlagName) => void;
  isOverridden: boolean;
}

export function FlagRow({
  name, def, currentValue, onUpdate, onReset, isOverridden,
}: FlagRowProps) {
  const shortName = name.split('.').pop() ?? name;

  return (
    <div className={`flag-row ${isOverridden ? 'overridden' : ''}`}>
      <div className="flag-info">
        <div className="flag-name-row">
          <code className="flag-key">{name}</code>
          {isOverridden && (
            <button className="flag-reset" onClick={() => onReset(name)} title="Reset to default">
              ↺
            </button>
          )}
        </div>
        <p className="flag-description">{def.description}</p>
      </div>

      <div className="flag-control">
        {def.type === 'bool' && (
          <label className="flag-toggle" id={`flag-${shortName}`}>
            <input
              type="checkbox"
              checked={currentValue as boolean}
              onChange={(e) => onUpdate(name, e.target.checked)}
            />
            <span className="flag-toggle-slider" />
            <span className="flag-toggle-label">
              {currentValue ? 'ON' : 'OFF'}
            </span>
          </label>
        )}

        {def.type === 'int' && (
          <div className="flag-int-control">
            <input
              type="range"
              min={def.min}
              max={def.max}
              value={currentValue as number}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                const result = validateFlagValue(def, v);
                onUpdate(name, result.coerced);
              }}
              className="flag-slider"
              id={`flag-${shortName}`}
            />
            <span className="flag-int-value">{String(currentValue)}</span>
            <span className="flag-int-range">{def.min}–{def.max}</span>
          </div>
        )}

        {def.type === 'enum' && (
          <select
            value={currentValue as string}
            onChange={(e) => onUpdate(name, e.target.value)}
            className="flag-select"
            id={`flag-${shortName}`}
          >
            {def.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
